import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';
import { initializeApp } from 'firebase/app';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider } from 'firebase/auth';

// --- FIREBASE CONFIGURATION ---
// TODO: Replace with your actual Firebase project config
// Note: You must retrieve these from your Firebase Console > Project Settings > General > Your Apps > Web App
const firebaseConfig = {
  apiKey: "AIzaSyAAQtP2GtSABqfQg4WUg0E1qIbF9sCYPqo",
  authDomain: "rk-waranty-web-app.firebaseapp.com",
  projectId: "rk-waranty-web-app",
  storageBucket: "rk-waranty-web-app.firebasestorage.app",
  messagingSenderId: "994090143064",
  appId: "1:994090143064:web:66ddfef5807d30f7be692d",
  measurementId: "G-E5RX4C5ENW"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Extend window interface for global recaptcha verifier
declare global {
  interface Window {
    recaptchaVerifier: any;
    confirmationResult: any;
  }
}

const ForgotPassword: React.FC = () => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'mobile' | 'otp' | 'password'>('mobile');

  useEffect(() => {
    // Initialize Recaptchaverifier on mount
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible',
        'callback': (response: any) => {
          // reCAPTCHA solved, allow signInWithPhoneNumber.
        },
        'expired-callback': () => {
          // Response expired. Ask user to solve reCAPTCHA again.
        }
      });
    }
  }, []);

  const handleSendOTP = async () => {
    if (!mobile) {
      setError('Please enter mobile number');
      return;
    }
    // Format number to E.164 format (e.g., +919999999999)
    // Assuming Indian numbers for now based on context, adjust as needed
    const phoneNumber = mobile.startsWith('+') ? mobile : `+91${mobile}`;

    setError('');
    setLoading(true);

    try {
      const appVerifier = window.recaptchaVerifier;
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      window.confirmationResult = confirmationResult;

      setStep('otp');
      setSuccess(`OTP sent to ${phoneNumber}`);
    } catch (err: any) {
      console.error("Error sending OTP", err);
      setError(err.message || 'Failed to send OTP. Try again.');
      // Reset recaptcha
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        // Re-init logic might be needed here or simple page refresh
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      setError('Please enter OTP');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const confirmationResult = window.confirmationResult;
      const result = await confirmationResult.confirm(otp);
      // User signed in successfully.
      const user = result.user;
      console.log("Verified User:", user);

      setStep('password');
      setSuccess('OTP Verified!');
    } catch (err: any) {
      setError('Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      // Get the ID token from the currently signed-in Firebase user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      const idToken = await currentUser.getIdToken();

      await axios.post('/api/auth/reset-password-verified', {
        idToken,
        newPassword: password
      });

      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (err: any) {
      console.error("Reset Error", err);
      setError(err.response?.data?.error || err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>🔑 Forgot Password</h2>
        <div id="recaptcha-container"></div>
        {step === 'mobile' && (
          <form onSubmit={(e) => { e.preventDefault(); handleSendOTP(); }}>
            <div className="form-group">
              <label>Mobile Number</label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="Enter mobile number"
                required
              />
            </div>
            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={(e) => { e.preventDefault(); handleVerifyOTP(); }}>
            <div className="form-group">
              <label>OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                required
                maxLength={6}
              />
            </div>
            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
              />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        <div className="auth-links">
          <Link to="/login">Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

